import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AddressFields, { emptyAddress } from '../components/AddressFields';
import CouponField from '../components/CouponField';
import BoardPreview from '../components/order/BoardPreview';
import CountStepper from '../components/order/CountStepper';
import LayoutPicker from '../components/order/LayoutPicker';
import OrderHeader from '../components/order/OrderHeader';
import OrderBar from '../components/order/OrderBar';
// The same paintings the homepage hero uses - see frontend/tiles/.
import TILE_POOL from '../tiles.generated.json';
import { fitCount, fitsParcel, layoutOptions, MAX_SPOTS, orient } from '../boardLayout';
import { getCatalog, createOrder, createCheckout } from '../api';
import { DEFAULT_RULES, orderEta, splitEta, parseDay } from '../delivery';
import './Order.css';

// No price is written down in this file. Canvas sizes, shipping fees and the
// board's own price all come from the API (/api/catalog) - they are edited
// in the admin panel. What's here is only the same sum backend/shop.py does,
// so the total can follow the form while it's being filled in. The server
// works the price out again when the order is placed, and refuses it if the
// two don't match.
const NO_PRICING = {
  pickup_fee_cents: 0,
  ship_to_curator_fee_cents: 0,
  canvas_postage_cents: 0,
};

// A split order still sends the board - with any canvases they hand out
// themselves - to the curator, plus postage for each canvas that goes to a
// painter of its own.
function shippingFeeCents(pricing, option, individualCanvases) {
  if (option === 'pickup') return pricing.pickup_fee_cents;
  if (option === 'ship_split') {
    return pricing.ship_to_curator_fee_cents + individualCanvases * pricing.canvas_postage_cents;
  }
  return pricing.ship_to_curator_fee_cents;
}

// Most common first. Pickup is last: it's the least explaining to do.
const SHIPPING_OPTION_IDS = ['ship_to_curator', 'ship_split', 'pickup'];
const STEP_KEYS = ['board', 'delivery', 'details', 'pay'];
const FORM_ID = 'of-form';
// Not a business limit - just where the preview stops being readable.
const MAX_PAINTERS = 200;

function shuffle(array) {
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// Today as a date input value, in local time (toISOString would be UTC,
// which is already tomorrow in the late evening here).
function todayValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Paying redirects the whole browser tab away to Mollie - if the customer
// comes back with the back button, React remounts from scratch and every
// useState reverts to its initial value. Without this, that wiped the
// entire form (including the order that had already been created) and a
// second "Pay" click submitted an empty one straight into a 422.
// sessionStorage survives that round trip; it's cleared once the order is
// actually paid (see ThankYou.js) so a finished order doesn't linger here.
const DRAFT_KEY = 'mosaik_order_draft';

function loadDraft() {
  try {
    return JSON.parse(sessionStorage.getItem(DRAFT_KEY)) ?? {};
  } catch {
    return {};
  }
}

function Order() {
  // Read when the page opens, not when the site first loads: a draft read
  // once at load time came back after the order was paid (the thank-you
  // page clears it, but too late for a copy already in memory), so "start
  // another Mosaik" opened on the old order.
  const [draft] = useState(loadDraft);
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === 'en' ? 'en-GB' : 'nl-NL';
  const [searchParams, setSearchParams] = useSearchParams();
  const stepParam = Number(searchParams.get('step'));
  const step = [2, 3, 4].includes(stepParam) ? stepParam : 1;

  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);

  const [numCanvases, setNumCanvases] = useState(draft.numCanvases ?? 16);
  const [sizeId, setSizeId] = useState(draft.sizeId ?? null);
  // The board shape picked from layoutOptions() - null means "the best one
  // for this many painters", which is also what it falls back to when the
  // count changes and the picked shape no longer fits.
  const [shape, setShape] = useState(draft.shape ?? null);
  const [orientation, setOrientation] = useState(draft.orientation ?? 'landscape');

  // One random order of the paintings per visit, so the board doesn't
  // reshuffle every time something re-renders.
  const [paintings] = useState(() => shuffle(TILE_POOL).map((file) => `/media/tiles/${file}`));

  const [eventName, setEventName] = useState(draft.eventName ?? '');
  const [deadline, setDeadline] = useState(draft.deadline ?? '');
  const [shippingOption, setShippingOption] = useState(draft.shippingOption ?? 'ship_to_curator');
  // On a split order: how many canvases get posted to a painter each. Null
  // until the customer picks a number, which means "everyone but you".
  const [individualChoice, setIndividualChoice] = useState(draft.individualChoice ?? null);
  const [curatorFirstName, setCuratorFirstName] = useState(draft.curatorFirstName ?? '');
  const [curatorLastName, setCuratorLastName] = useState(draft.curatorLastName ?? '');
  const [curatorEmail, setCuratorEmail] = useState(draft.curatorEmail ?? '');
  const [curatorEmailConfirm, setCuratorEmailConfirm] = useState(draft.curatorEmailConfirm ?? '');
  const [curatorAddress, setCuratorAddress] = useState(draft.curatorAddress ?? emptyAddress);
  const [coupon, setCoupon] = useState(draft.coupon ?? null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Restoring this is what stops "back from Mollie, click Pay again" from
  // creating a second (blank) order - handlePayNow below only calls
  // createOrder when there isn't one yet, or the form has changed since.
  const [result, setResult] = useState(draft.result ?? null);

  // Also called again when an order is refused because a price changed
  // while this page was open, so the form shows the new numbers.
  const loadCatalog = useCallback(() => {
    getCatalog()
      .then((data) => {
        setCatalog(data);
        // A draft can point at a size that has since been taken off sale.
        setSizeId((current) =>
          data.canvas_sizes.some((s) => s.id === current) ? current : data.canvas_sizes[0]?.id ?? null
        );
      })
      .catch((err) => setCatalogError(err.message));
  }, []);

  useEffect(loadCatalog, [loadCatalog]);

  // Keeps the draft in sessionStorage in step with the form, so a round
  // trip to Mollie and back (a real cross-origin navigation, not a client
  // route change - React remounts from scratch) restores exactly what was
  // there before.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({
          numCanvases,
          sizeId,
          shape,
          orientation,
          eventName,
          deadline,
          shippingOption,
          individualChoice,
          curatorFirstName,
          curatorLastName,
          curatorEmail,
          curatorEmailConfirm,
          curatorAddress,
          coupon,
          result,
        })
      );
    } catch {
      // Storage disabled (private browsing, quota) - the form still works,
      // it just won't survive a trip to Mollie and back.
    }
  }, [
    numCanvases,
    sizeId,
    shape,
    orientation,
    eventName,
    deadline,
    shippingOption,
    individualChoice,
    curatorFirstName,
    curatorLastName,
    curatorEmail,
    curatorEmailConfirm,
    curatorAddress,
    coupon,
    result,
  ]);

  const formatMoney = (cents) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(cents / 100);
  const longDate = (value) =>
    value.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });

  const canvasSizes = catalog?.canvas_sizes ?? [];
  const size = canvasSizes.find((s) => s.id === sizeId) ?? null;
  // Sizes are named in the admin panel, in English and optionally in Dutch.
  const sizeLabel = (s) => (i18n.resolvedLanguage === 'nl' && s?.label_nl) || s?.label;

  // Boards are cut to size from stock sheets, and the catalog lists those
  // with what a board cut from each costs. A shape's price is the cheapest
  // sheet it fits on, so one more canvas can move the board - and its price -
  // up to the bigger sheet.
  const sheets = useMemo(() => catalog?.boards ?? [], [catalog]);
  // The most painters one board can have at this canvas size: what the
  // biggest sheet carries, and never more than the server accepts.
  const maxPainters = useMemo(
    () => (size && sheets.length > 0 ? fitCount(MAX_SPOTS, size.size_cm, sheets) : MAX_PAINTERS),
    [size, sheets]
  );
  const shapeOptions = useMemo(
    () => (size ? layoutOptions(numCanvases, size.size_cm, sheets) : []),
    [numCanvases, size, sheets]
  );
  const shapeChoice =
    shapeOptions.find((o) => shape && o.cols === shape.cols && o.rows === shape.rows) ??
    shapeOptions[0] ?? { cols: 1, rows: 1, spare: 0, sheet: null };
  const isSquare = shapeChoice.cols === shapeChoice.rows;
  const { cols, rows } = orient(shapeChoice, isSquare ? 'landscape' : orientation);
  // Spots this shape still has room for. We only build full boards, so
  // these have to become canvases before the order can go on.
  const leftover = shapeChoice.spare;
  // The board is cut to its grid: as many canvases across and down as it has.
  const boardWidthCm = size ? cols * size.size_cm : 0;
  const boardHeightCm = size ? rows * size.size_cm : 0;

  // A bigger canvas fits fewer on the biggest sheet: keep the count to what
  // makes a board.
  useEffect(() => {
    if (size && sheets.length > 0 && shapeOptions.length === 0) {
      setNumCanvases(fitCount(numCanvases, size.size_cm, sheets));
    }
  }, [size, sheets, shapeOptions, numCanvases]);

  // Too big for the biggest parcel, packing included: the website only
  // sells it as a pickup. Delivery is arranged by hand (a custom order).
  const tooBigToPost = size !== null && !fitsParcel(catalog?.parcel, boardWidthCm, boardHeightCm);
  useEffect(() => {
    if (tooBigToPost && shippingOption !== 'pickup') setShippingOption('pickup');
  }, [tooBigToPost, shippingOption]);

  const pricing = catalog?.pricing ?? NO_PRICING;
  const canvasSubtotalCents = size ? size.price_cents * numCanvases : 0;
  // The board's own price, from the sheet it is cut from. 0 hides the line.
  const boardCents = shapeChoice.sheet ? shapeChoice.sheet.price_cents : 0;

  const emailMismatch =
    curatorEmailConfirm.length > 0 &&
    curatorEmail.trim().toLowerCase() !== curatorEmailConfirm.trim().toLowerCase();

  const shippingOptionIds = tooBigToPost ? ['pickup'] : SHIPPING_OPTION_IDS;
  // The only mode where anyone besides the curator is involved - drives
  // whether a deadline/share-link is shown anywhere in the flow.
  const isGroupOrder = shippingOption === 'ship_split';
  // How many canvases are posted to painters is the curator's call, from one
  // up to all of them (then only the board comes to them) - kept in range as
  // the painter count changes.
  const maxIndividual = Math.max(1, numCanvases);
  const individualCanvases = Math.min(maxIndividual, Math.max(1, individualChoice ?? maxIndividual));
  const withBoardCanvases = numCanvases - individualCanvases;

  // Concrete dates rather than "within 2 days", so the curator can plan
  // against the date of their event. Rules come from the API so they can't
  // drift from the backend's - see src/delivery.js.
  const deliveryRules = catalog?.delivery ?? DEFAULT_RULES;
  // On a split order the board and the very last canvas land on the same
  // day: someone joining on the deadline itself has theirs posted and
  // delivered in the same number of delivery days the board takes.
  const splitArrival = splitEta(deadline, deliveryRules);
  const soloArrival = orderEta(deliveryRules);

  const shippingCents = shippingFeeCents(pricing, shippingOption, individualCanvases);
  // What a coupon is worked out against - matches how the API applies it.
  const subtotalCents = boardCents + canvasSubtotalCents + shippingCents;
  const discountCents = coupon?.discount_cents ?? 0;
  const estimatedTotalCents = subtotalCents - discountCents;

  // Adds just enough painters to fill the chosen shape exactly.
  function makeFull() {
    setShape({ cols: shapeChoice.cols, rows: shapeChoice.rows });
    setNumCanvases(Math.min(MAX_PAINTERS, shapeChoice.cols * shapeChoice.rows));
  }

  function goToStep(nextStep) {
    setError(null);
    setSearchParams(nextStep === 1 ? {} : { step: String(nextStep) });
    window.scrollTo({ top: 0 });
  }

  // The order row is only written when the customer commits to paying, and
  // it's kept in state so a failed hand-off to Mollie can be retried without
  // creating a second order.
  async function handlePayNow() {
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        event_name: eventName,
        num_canvases: numCanvases,
        canvas_size_id: sizeId,
        grid_rows: rows,
        grid_cols: cols,
        // The board's size in cm isn't sent: the server works it out from
        // the grid and the canvas size.
        deadline: isGroupOrder ? deadline : null,
        shipping_option: shippingOption,
        individual_canvases: isGroupOrder ? individualCanvases : null,
        curator_first_name: curatorFirstName,
        curator_last_name: curatorLastName,
        curator_email: curatorEmail,
        curator_address: shippingOption === 'pickup' ? null : curatorAddress,
        coupon_code: coupon?.code ?? null,
        // The total on the button. The server charges what it works out
        // itself; this lets it say so when that's a different amount.
        expected_total_cents: estimatedTotalCents,
      };
      // The saved order is only good for exactly what it was placed with.
      // Someone who comes back from Mollie and changes the board has to get
      // a new order - reusing the old one would show the new price on the
      // button and charge the old one.
      const placedWith = JSON.stringify(payload);

      let order = result?.placedWith === placedWith ? result : null;
      if (!order) {
        order = {
          ...(await createOrder({
            ...payload,
            // Drives the confirmation email's language and which language
            // Mollie's hosted checkout opens in. Left out of the comparison
            // above: switching language isn't a different order.
            language: i18n.resolvedLanguage,
          })),
          placedWith,
        };
        setResult(order);
      }

      const { checkout_url } = await createCheckout(order.public_id, order.curator_token);
      window.location.href = checkout_url;
    } catch (err) {
      // 409: a price or the stock changed since this page loaded. Fetch the
      // current numbers so the form shows what the next attempt will cost.
      if (err.status === 409) loadCatalog();
      setError(err.message);
      setSubmitting(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (step === 1) {
      if (leftover === 0) goToStep(2);
    } else if (step === 2) {
      if (!isGroupOrder || deadline) goToStep(3);
    } else if (step === 3) {
      // Everything for this order is sent to this address, so a typo here
      // is expensive - hence typing it twice.
      if (curatorEmail.trim().toLowerCase() !== curatorEmailConfirm.trim().toLowerCase()) {
        setError(t('order.errors.emailMismatch'));
        return;
      }
      goToStep(4);
    } else {
      handlePayNow();
    }
  }

  const steps = STEP_KEYS.map((key) => t(`order.steps.${key}`));
  const stepOf = t('order.steps.stepOf', { current: step, total: STEP_KEYS.length, label: steps[step - 1] });

  if (catalogError || !catalog || !size) {
    return (
      <div className="atelier of">
        <OrderHeader />
        <p className={`of-status-page ${catalogError ? 'of-error' : ''}`}>
          {catalogError ? t('order.catalogError', { error: catalogError }) : t('common.loading')}
        </p>
      </div>
    );
  }

  const priceRows = (
    <dl className="of-prices">
      {boardCents > 0 && (
        <div>
          <dt>{t('order.price.board', { width: boardWidthCm, height: boardHeightCm })}</dt>
          <dd>{formatMoney(boardCents)}</dd>
        </div>
      )}
      <div>
        <dt>{t('order.price.canvases', { count: numCanvases, price: formatMoney(size.price_cents) })}</dt>
        <dd>{formatMoney(canvasSubtotalCents)}</dd>
      </div>
      {isGroupOrder ? (
        <>
          <div>
            <dt>{t('order.price.boardShipping')}</dt>
            <dd>{formatMoney(pricing.ship_to_curator_fee_cents)}</dd>
          </div>
          <div>
            <dt>
              {t('order.price.splitShipping', {
                count: individualCanvases,
                price: formatMoney(pricing.canvas_postage_cents),
              })}
            </dt>
            <dd>{formatMoney(individualCanvases * pricing.canvas_postage_cents)}</dd>
          </div>
        </>
      ) : (
        <div>
          <dt>{t(`order.price.shipping.${shippingOption}`)}</dt>
          <dd>{shippingCents === 0 ? t('order.delivery.free') : formatMoney(shippingCents)}</dd>
        </div>
      )}
      {discountCents > 0 && (
        <div className="of-prices-discount">
          <dt>{t('common.discount', { code: coupon.code })}</dt>
          <dd>&minus;{formatMoney(discountCents)}</dd>
        </div>
      )}
      <div className="of-prices-total">
        <dt>{t('order.price.total')}</dt>
        <dd>{formatMoney(estimatedTotalCents)}</dd>
      </div>
    </dl>
  );

  const optionPrice = (id) => {
    if (id === 'ship_split') {
      return (
        <>
          {formatMoney(pricing.ship_to_curator_fee_cents)}
          <small>{t('order.delivery.perCanvas', { price: formatMoney(pricing.canvas_postage_cents) })}</small>
        </>
      );
    }
    const fee = shippingFeeCents(pricing, id, 0);
    return fee === 0 ? t('order.delivery.free') : formatMoney(fee);
  };

  let title;
  let lede;
  let body;
  let nextLabel = t('order.next');
  let nextDisabled = false;
  let hint = null;

  if (step === 1) {
    title = t('order.board.title');
    lede = t('order.board.lede');
    if (leftover > 0) {
      nextDisabled = true;
      hint = t('order.hints.fillBoard', { count: leftover });
    }

    body = (
      <>
        <section className="of-section">
          <p className="of-kicker">
            <span>01</span> {t('order.board.count.kicker')}
          </p>
          <h2 className="of-question">{t('order.board.count.question')}</h2>
          <CountStepper
            value={numCanvases}
            min={1}
            max={maxPainters}
            onChange={setNumCanvases}
            lessLabel={t('order.board.count.less')}
            moreLabel={t('order.board.count.more')}
            inputLabel={t('order.board.count.label')}
          />
          <p className="of-hint">
            {numCanvases >= maxPainters && sheets.length > 0
              ? t('order.board.count.max', { count: maxPainters })
              : t('order.board.count.hint')}
          </p>
        </section>

        <section className="of-section">
          <p className="of-kicker">
            <span>02</span> {t('order.board.size.kicker')}
          </p>
          <h2 className="of-question">{t('order.board.size.question')}</h2>
          <div className="of-options" role="radiogroup">
            {canvasSizes.map((option) => {
              const active = option.id === sizeId;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`of-option ${active ? 'is-selected' : ''}`}
                  onClick={() => setSizeId(option.id)}
                >
                  <span className="of-radio" aria-hidden="true" />
                  {/* Drawn at the canvas's relative size, so the sizes read
                      as smaller / bigger before any number does. */}
                  <span className="of-size-icon-box" aria-hidden="true">
                    <span
                      className="of-size-icon"
                      style={{ width: option.size_cm * 1.5, height: option.size_cm * 1.5 }}
                    />
                  </span>
                  <span className="of-option-main">
                    <span className="of-option-title">{sizeLabel(option)}</span>
                    <span className="of-option-desc">
                      {option.size_cm} &times; {option.size_cm} cm
                    </span>
                  </span>
                  <span className="of-option-price">
                    {formatMoney(option.price_cents)}
                    <small>{t('order.board.size.perCanvas')}</small>
                  </span>
                </button>
              );
            })}
          </div>
          <p className="of-hint">{t('order.board.size.hint')}</p>
        </section>

        <section className="of-section">
          <p className="of-kicker">
            <span>03</span> {t('order.board.shape.kicker')}
          </p>
          <h2 className="of-question">{t('order.board.shape.question')}</h2>
          <LayoutPicker
            options={shapeOptions}
            selected={shapeChoice}
            sizeCm={size.size_cm}
            formatMoney={formatMoney}
            orientation={orientation}
            onSelect={(option) => setShape({ cols: option.cols, rows: option.rows })}
            onOrientation={setOrientation}
            t={t}
          />
          {leftover > 0 && (
            <div className="of-explain" role="status">
              <p>
                {t('order.board.spare.explain', {
                  count: leftover,
                  painters: numCanvases,
                  cols,
                  rows,
                })}
              </p>
              <button type="button" className="of-fill-btn" onClick={makeFull}>
                {t('order.board.spare.add', { count: leftover })}
                <small>{t('order.board.spare.total', { count: cols * rows })}</small>
              </button>
            </div>
          )}
        </section>
      </>
    );
  } else if (step === 2) {
    title = t('order.delivery.title');
    lede = t('order.delivery.lede');
    if (isGroupOrder && !deadline) {
      nextDisabled = true;
      hint = t('order.hints.pickDeadline');
    }

    body = (
      <section className="of-section">
        {tooBigToPost && (
          <div className="of-explain of-explain--top" role="status">
            <p>{t('order.delivery.tooBig.text', { width: boardWidthCm, height: boardHeightCm })}</p>
            <p>
              <Link to="/contact">{t('order.delivery.tooBig.contact')}</Link>
            </p>
          </div>
        )}
        <div className="of-dcards" role="radiogroup">
          {shippingOptionIds.map((id) => {
            const active = shippingOption === id;
            return (
              <div key={id} className={`of-dcard ${active ? 'is-selected' : ''}`}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className="of-dcard-head"
                  onClick={() => setShippingOption(id)}
                >
                  <span className="of-radio" aria-hidden="true" />
                  <span className="of-option-main">
                    <span className="of-option-title">{t(`order.delivery.options.${id}.title`)}</span>
                    <span className="of-option-desc">{t(`order.delivery.options.${id}.desc`)}</span>
                  </span>
                  <span className="of-option-price">{optionPrice(id)}</span>
                </button>

                {active && (
                  <div className="of-dcard-body">
                    {id === 'ship_split' ? (
                      <>
                        {/* The two things to decide, and nothing else. */}
                        <dl className="of-flow">
                          <dt>{t('order.delivery.flow.post')}</dt>
                          <dd className="of-flow-pick">
                            <CountStepper
                              small
                              value={individualCanvases}
                              min={1}
                              max={maxIndividual}
                              onChange={setIndividualChoice}
                              lessLabel={t('order.delivery.split.less')}
                              moreLabel={t('order.delivery.split.more')}
                              inputLabel={t('order.delivery.flow.byPostLabel')}
                            />
                            <span>{t('order.delivery.flow.ofCanvases', { count: numCanvases })}</span>
                          </dd>

                          <dt>{t('order.delivery.flow.signUp')}</dt>
                          <dd>
                            <input
                              type="date"
                              className="of-flow-date"
                              value={deadline}
                              min={todayValue()}
                              onChange={(e) => setDeadline(e.target.value)}
                              aria-label={t('order.delivery.deadline.label')}
                              required
                            />
                          </dd>
                        </dl>
                        <p className="of-dcard-note">
                          {withBoardCanvases === 0
                            ? t('order.delivery.flow.boardOnly')
                            : t('order.delivery.flow.boardWith', { count: withBoardCanvases })}{' '}
                          {splitArrival
                            ? t('order.delivery.flow.splitArrives', { date: longDate(splitArrival) })
                            : t('order.delivery.flow.splitSoon')}
                        </p>
                      </>
                    ) : (
                      <p className="of-dcard-note of-dcard-note--date">
                        {id === 'pickup'
                          ? t('order.delivery.flow.readyFrom', { date: longDate(soloArrival) })
                          : t('order.delivery.flow.arrives', { date: longDate(soloArrival) })}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    );
  } else if (step === 3) {
    title = t('order.details.title');
    lede = t('order.details.lede');

    body = (
      <>
        <section className="of-section">
          <p className="of-kicker">
            <span>01</span> {t('order.details.occasion.kicker')}
          </p>
          <h2 className="of-question">{t('order.details.occasion.question')}</h2>
          <label className="field">
            {t('order.details.occasion.label')}
            <input
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              placeholder={t('order.details.occasion.placeholder')}
              required
            />
          </label>
          <p className="of-hint">
            {isGroupOrder ? t('order.details.occasion.hintGroup') : t('order.details.occasion.hintRegular')}
          </p>
        </section>

        <section className="of-section">
          <p className="of-kicker">
            <span>02</span> {t('order.details.you.kicker')}
          </p>
          <h2 className="of-question">{t('order.details.you.question')}</h2>
          <div className="field-row">
            <label className="field">
              {t('common.firstName')}
              <input
                value={curatorFirstName}
                onChange={(e) => setCuratorFirstName(e.target.value)}
                autoComplete="given-name"
                required
              />
            </label>
            <label className="field">
              {t('common.surname')}
              <input
                value={curatorLastName}
                onChange={(e) => setCuratorLastName(e.target.value)}
                autoComplete="family-name"
                required
              />
            </label>
          </div>
          <label className="field">
            {t('common.email')}
            <input
              type="email"
              value={curatorEmail}
              onChange={(e) => setCuratorEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </label>
          <label className="field">
            {t('order.details.you.emailConfirm')}
            <input
              type="email"
              value={curatorEmailConfirm}
              onChange={(e) => setCuratorEmailConfirm(e.target.value)}
              onPaste={(e) => e.preventDefault()}
              autoComplete="off"
              required
            />
          </label>
          {emailMismatch ? (
            <p className="of-error">{t('order.errors.emailMismatch')}</p>
          ) : (
            <p className="of-hint">
              {isGroupOrder ? t('order.details.you.hintGroup') : t('order.details.you.hintRegular')}
            </p>
          )}
        </section>

        {shippingOption !== 'pickup' && (
          <section className="of-section">
            <p className="of-kicker">
              <span>03</span> {t('order.details.address.kicker')}
            </p>
            <h2 className="of-question">
              {isGroupOrder
                ? t('order.details.address.questionSplit')
                : t('order.details.address.questionCurator')}
            </h2>
            <AddressFields address={curatorAddress} onChange={setCuratorAddress} />
            {isGroupOrder && <p className="of-hint">{t('order.details.address.hintSplit')}</p>}
          </section>
        )}
      </>
    );
  } else {
    title = t('order.pay.title');
    lede = t('order.pay.lede');
    nextLabel = submitting ? t('order.pay.buttonLoading') : t('order.pay.button', { amount: formatMoney(estimatedTotalCents) });

    const reviewHead = (label, target) => (
      <div className="of-review-head">
        <h2>{label}</h2>
        <button type="button" className="of-link" onClick={() => goToStep(target)}>
          {t('order.pay.change')}
        </button>
      </div>
    );

    body = (
      <>
        <section className="of-section of-review">
          <div className="of-review-block">
            {reviewHead(t('order.steps.board'), 1)}
            <dl>
              <div>
                <dt>{t('order.pay.board.painters')}</dt>
                <dd>{t('order.pay.board.paintersValue', { count: numCanvases })}</dd>
              </div>
              <div>
                <dt>{t('order.pay.board.canvas')}</dt>
                <dd>
                  {sizeLabel(size)} &middot; {size.size_cm} &times; {size.size_cm} cm
                </dd>
              </div>
              <div>
                <dt>{t('order.pay.board.board')}</dt>
                <dd>
                  {cols} &times; {rows} &middot; {boardWidthCm} &times; {boardHeightCm} cm
                </dd>
              </div>
            </dl>
          </div>

          <div className="of-review-block">
            {reviewHead(t('order.steps.delivery'), 2)}
            <dl>
              <div>
                <dt>{t('order.pay.delivery.how')}</dt>
                <dd>{t(`order.delivery.options.${shippingOption}.title`)}</dd>
              </div>
              {isGroupOrder ? (
                <>
                  <div>
                    <dt>{t('order.pay.delivery.split')}</dt>
                    <dd>
                      {t(withBoardCanvases > 0 ? 'order.pay.delivery.splitValue' : 'order.pay.delivery.splitAll', {
                        count: individualCanvases,
                        rest: withBoardCanvases,
                      })}
                    </dd>
                  </div>
                  <div>
                    <dt>{t('order.pay.delivery.deadline')}</dt>
                    <dd>{longDate(parseDay(deadline))}</dd>
                  </div>
                  <div>
                    <dt>{t('order.pay.delivery.everythingBy')}</dt>
                    <dd>{splitArrival && longDate(splitArrival)}</dd>
                  </div>
                </>
              ) : (
                <div>
                  <dt>
                    {shippingOption === 'pickup' ? t('order.pay.delivery.readyFrom') : t('order.pay.delivery.arrives')}
                  </dt>
                  <dd>{longDate(soloArrival)}</dd>
                </div>
              )}
            </dl>
          </div>

          <div className="of-review-block">
            {reviewHead(t('order.steps.details'), 3)}
            <dl>
              <div>
                <dt>{t('order.pay.details.occasion')}</dt>
                <dd>{eventName}</dd>
              </div>
              <div>
                <dt>{t('order.pay.details.name')}</dt>
                <dd>
                  {curatorFirstName} {curatorLastName}
                </dd>
              </div>
              <div>
                <dt>{t('common.email')}</dt>
                <dd>{curatorEmail}</dd>
              </div>
              {shippingOption !== 'pickup' && (
                <div>
                  <dt>{t('order.pay.details.address')}</dt>
                  <dd>
                    {curatorAddress.street} {curatorAddress.house_number}, {curatorAddress.postal_code}{' '}
                    {curatorAddress.city}, {curatorAddress.country}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </section>

        <section className="of-section">
          <h2 className="of-question">{t('order.pay.pricesTitle')}</h2>
          {priceRows}
          <CouponField
            subtotalCents={subtotalCents}
            applied={coupon}
            onApply={setCoupon}
            onRemove={() => setCoupon(null)}
          />
          <p className="of-hint">{t('order.pay.mollie')}</p>
        </section>
      </>
    );
  }

  return (
    <div className="atelier of">
      <OrderHeader step={step} steps={steps} onGoTo={goToStep} stepOfLabel={stepOf} />

      <main className="of-main">
        <section className="of-visual" aria-label={t('order.preview.title')}>
          <div className="of-visual-stage">
            <BoardPreview
              cols={cols}
              rows={rows}
              numCanvases={numCanvases}
              sizeCm={size.size_cm}
              paintings={paintings}
              youLabel={t('order.preview.you')}
              label={t('order.preview.facts', {
                count: numCanvases,
                width: boardWidthCm,
                height: boardHeightCm,
                cols,
                rows,
              })}
            />
          </div>

          <p className="of-visual-caption">
            {step >= 3 && eventName && <em>{eventName}</em>}
            <span>
              {t('order.preview.facts', {
                count: numCanvases,
                width: boardWidthCm,
                height: boardHeightCm,
                cols,
                rows,
              })}
            </span>
          </p>
        </section>

        <form id={FORM_ID} key={step} className="of-panel" onSubmit={handleSubmit}>
          <header className="of-intro">
            <h1 className="of-title">{title}</h1>
            <p className="of-lede">{lede}</p>
          </header>
          {body}
        </form>
      </main>

      <OrderBar
        // A fresh bar per step, so an open breakdown doesn't follow you along.
        key={step}
        total={formatMoney(estimatedTotalCents)}
        totalLabel={t('order.bar.total')}
        facts={t('order.bar.facts', { count: numCanvases, size: sizeLabel(size) })}
        breakdown={step < 4 ? priceRows : null}
        breakdownLabel={t('order.bar.breakdown')}
        backLabel={t('order.back')}
        onBack={step > 1 ? () => goToStep(step - 1) : null}
        nextLabel={nextLabel}
        nextDisabled={nextDisabled}
        busy={submitting}
        hint={hint}
        error={error}
        formId={FORM_ID}
      />
    </div>
  );
}

export default Order;
